import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/AuthContext';
import { apiService } from '../services/api';
import AuthModals from '../components/AuthModals';

const JoinLeaguePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
  const [hasAttemptedJoin, setHasAttemptedJoin] = useState(false);

  const code = searchParams.get('code');
  const passcode = searchParams.get('passcode');
  const typeRaw = searchParams.get('type') || '';
  const type = typeRaw ? typeRaw.toLowerCase().replace(/\s+/g, '_') : 'budget_league'; // pickem, eliminator, budget_league (default for share links)

  useEffect(() => {
    if (authLoading) return;

    if (!code) {
      setError('Invalid join link. Missing join code.');
      setLoading(false);
      return;
    }

    if (isAuthenticated && user && !hasAttemptedJoin) {
      setLoading(false);
      handleJoinLeague();
    } else if (!isAuthenticated) {
      setIsLoginModalOpen(true);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, user, code, hasAttemptedJoin]);

  const handleJoinLeague = async () => {
    if (!code || hasAttemptedJoin) {
      return;
    }

    setHasAttemptedJoin(true);
    setJoining(true);
    setError(null);
    setIsLoginModalOpen(false);
    setIsRegisterModalOpen(false);

    try {
      if (type === 'pickem') {
        const response = await apiService.joinPickemLeagueByCode(code, passcode || undefined);
        if (response.success) {
          // Navigate to the league page
          const leagueId = response.leagueId || response.league?.id;
          if (leagueId) {
            navigate(`/pickem-league/${leagueId}`);
            return;
          } else {
            throw new Error('League ID not found in response');
          }
        } else {
          throw new Error(response.message || 'Failed to join league');
        }
      } else if (type === 'eliminator') {
        const response = await apiService.joinEliminatorLeagueByCode(code, passcode || undefined);
        if (response.success) {
          const leagueId = response.leagueId || response.league?.id;
          if (leagueId) {
            navigate(`/eliminator-league/${leagueId}`);
            return;
          } else {
            throw new Error('League ID not found in response');
          }
        } else {
          throw new Error(response.message || 'Failed to join league');
        }
      } else if (type === 'h2h') {
        const response = await apiService.joinLeagueByCode(code, passcode || undefined);
        const data = response?.result ?? response;
        if (data?.success || response?.success) {
          const leagueId = data?.league?.id ?? response?.leagueId ?? response?.league?.id;
          if (leagueId) {
            navigate(`/h2h-league/${leagueId}`);
            return;
          }
          throw new Error('League ID not found in response');
        }
        throw new Error((data?.message ?? response?.message) || 'Failed to join league');
      } else {
        // Budget league / fantasy: budget_league, fantasy, budgetleague, or any other type from share link
        const response = await apiService.joinLeagueByCode(code, passcode || undefined);
        const data = response?.result ?? response;
        if (data?.success || response?.success) {
          const leagueId = data?.league?.id ?? response?.leagueId ?? response?.league?.id;
          if (leagueId) {
            navigate(`/budget-league/${leagueId}`);
            return;
          }
          throw new Error('League ID not found in response');
        }
        throw new Error((data?.message ?? response?.message) || 'Failed to join league');
      }
    } catch (err: any) {
      console.error('Error joining league:', err);
      setError(err.message || 'Failed to join league. Please check the link and try again.');
      setHasAttemptedJoin(false);
    } finally {
      setJoining(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A1833] via-[#13294B] to-[#4FC3F7] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (error && !isLoginModalOpen && !isRegisterModalOpen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A1833] via-[#13294B] to-[#4FC3F7] flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20 shadow-lg max-w-md w-full">
          <div className="text-red-300 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-4 text-center">Unable to Join League</h2>
          <p className="text-blue-200 mb-6 text-center">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-lg font-semibold transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-[#0A1833] via-[#13294B] to-[#4FC3F7] flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20 shadow-lg max-w-md w-full">
          {joining ? (
            <div className="text-center">
              <div className="text-white text-xl mb-4">Joining league...</div>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
            </div>
          ) : (
            <div className="text-center">
              <div className="text-white text-xl mb-4">Please log in or sign up to join this league</div>
            </div>
          )}
        </div>
      </div>
      <AuthModals
        isLoginModalOpen={isLoginModalOpen}
        isRegisterModalOpen={isRegisterModalOpen}
        isForgotPasswordModalOpen={isForgotPasswordModalOpen}
        closeModals={() => {
          setIsLoginModalOpen(false);
          setIsRegisterModalOpen(false);
          setIsForgotPasswordModalOpen(false);
          navigate('/');
        }}
        onSuccessfulAuth={() => {
          setIsLoginModalOpen(false);
          setIsRegisterModalOpen(false);
          setIsForgotPasswordModalOpen(false);
        }}
        openRegisterModal={() => {
          setIsLoginModalOpen(false);
          setIsRegisterModalOpen(true);
        }}
        openLoginModal={() => {
          setIsRegisterModalOpen(false);
          setIsLoginModalOpen(true);
        }}
        openForgotPasswordModal={() => {
          setIsLoginModalOpen(false);
          setIsForgotPasswordModalOpen(true);
        }}
      />
    </>
  );
};

export default JoinLeaguePage;

